/**
 * DOCReportView Component (Hybrid Approach v1)
 *
 * Renders financial data using the hardcoded DOC template structure.
 * Maps raw account values from the API to the template categories.
 *
 * Features:
 * - Uses DOC template for structure (frontend/src/config/docTemplate.ts)
 * - Displays accounts grouped by department and expense category
 * - Shows monthly breakdown with YTD totals
 * - Calculates totals based on actual account values
 */

import React, { useState, useMemo } from 'react';
import { MONTH_NAMES, getMonthValue, createEmptyMonthlyTotals } from '../../types';
import {
  DOC_TEMPLATE,
  buildAccountLookup,
  resolveAccountsForDepartment,
} from '../../config/docTemplate';
import { addMonthlyTotals, subtractMonthlyTotals, formatPercent } from './calculations';
import { calculateDepartmentTotals, renderDepartment } from './renderers';
import { AccountData, DOCReportViewProps } from './types';

export const DOCReportView: React.FC<DOCReportViewProps> = ({
  locationName,
  locationId,
  year,
  accounts,
  docGross,
  unitCountsData,
  formatCurrency,
  assignedDepartmentIds,
}) => {
  // Track expanded departments and categories
  const [expandedDepartments, setExpandedDepartments] = useState<Set<string>>(new Set());
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  // Toggle to include Others Department in Total Gross (default: on)
  const [includeOthersInGross, setIncludeOthersInGross] = useState<boolean>(true);

  // Build account lookup map (available for future use)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const accountLookup = useMemo(() => buildAccountLookup(), []);

  // Create a map of account code to data
  const accountDataMap = useMemo(() => {
    const map = new Map<string, AccountData>();
    accounts.forEach(acc => map.set(acc.account, acc));
    return map;
  }, [accounts]);

  // Create DOC gross lookup
  const docGrossMap = useMemo(() => {
    const map = new Map<string, typeof docGross[0]>();
    docGross.forEach(dg => map.set(dg.department, dg));
    return map;
  }, [docGross]);

  // Department filtering for DepartmentManagers
  const isDeptRestricted = assignedDepartmentIds && assignedDepartmentIds.length > 0;
  const visibleDepartments = useMemo(() => {
    if (!isDeptRestricted) return DOC_TEMPLATE;
    return DOC_TEMPLATE.filter(dept => assignedDepartmentIds!.includes(dept.name));
  }, [assignedDepartmentIds, isDeptRestricted]);

  // Toggle handlers
  const toggleDepartment = (deptName: string) => {
    setExpandedDepartments(prev => {
      const newSet = new Set(prev);
      if (newSet.has(deptName)) {
        newSet.delete(deptName);
      } else {
        newSet.add(deptName);
      }
      return newSet;
    });
  };

  const toggleCategory = (key: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  // Calculate grand totals across all departments
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const grandTotals = useMemo(() => {
    let totalGross = { ytd: 0, monthly: createEmptyMonthlyTotals() };
    let totalExpenses = { ytd: 0, monthly: createEmptyMonthlyTotals() };
    let othersGross = { ytd: 0, monthly: createEmptyMonthlyTotals() };

    const availableAccountCodes = Array.from(accountDataMap.keys());
    DOC_TEMPLATE.forEach(dept => {
      // Resolve suffix-based pattern matching for grand totals (discovers new Body Shop accounts)
      const resolvedCategories = resolveAccountsForDepartment(dept, availableAccountCodes);
      const resolvedDept = resolvedCategories !== dept.expenseCategories
        ? { ...dept, expenseCategories: resolvedCategories }
        : dept;
      const deptTotals = calculateDepartmentTotals(resolvedDept, accountDataMap, docGrossMap);

      if (dept.name === 'OTHERS') {
        othersGross.ytd = deptTotals.totalGross.ytd;
        othersGross.monthly = deptTotals.totalGross.monthly;
        if (includeOthersInGross) {
          totalGross.ytd += deptTotals.totalGross.ytd;
          totalGross.monthly = addMonthlyTotals(totalGross.monthly, deptTotals.totalGross.monthly);
        }
        return;
      }

      if (dept.name === 'RENTAL') {
        totalGross.ytd += deptTotals.rentalIncome.ytd;
        totalGross.monthly = addMonthlyTotals(totalGross.monthly, deptTotals.rentalIncome.monthly);
        totalExpenses.ytd += deptTotals.totalExpenses.ytd;
        totalExpenses.monthly = addMonthlyTotals(totalExpenses.monthly, deptTotals.totalExpenses.monthly);
        return;
      }

      totalGross.ytd += deptTotals.totalGross.ytd;
      totalGross.monthly = addMonthlyTotals(totalGross.monthly, deptTotals.totalGross.monthly);
      totalExpenses.ytd += deptTotals.totalExpenses.ytd;
      totalExpenses.monthly = addMonthlyTotals(totalExpenses.monthly, deptTotals.totalExpenses.monthly);
    });

    const operatingProfit = {
      ytd: totalGross.ytd - totalExpenses.ytd,
      monthly: subtractMonthlyTotals(totalGross.monthly, totalExpenses.monthly),
    };

    return { totalGross, totalExpenses, operatingProfit, othersGross };
  }, [accounts, docGross, includeOthersInGross]);

  return (
    <div className="space-y-4">
      {/* Location Summary Header - hidden for dept-restricted users */}
      {!isDeptRestricted && <div className="bg-white rounded-lg shadow mb-6">
        <div className="px-3 md:px-6 py-3 md:py-4 border-b border-gray-200 bg-gray-800">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h2 className="text-lg md:text-xl font-bold text-white">
                {locationName} - YTD {year}
              </h2>
              <p className="text-sm text-gray-300 mt-1">
                DOC Report View (Hybrid) - {accounts.length} accounts loaded
              </p>
            </div>
            <div className="mt-3 md:mt-0 flex items-center">
              <label className="flex items-center cursor-pointer">
                <span className="text-sm text-gray-300 mr-3">Include Others in Gross</span>
                <div className="relative">
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={includeOthersInGross}
                    onChange={(e) => setIncludeOthersInGross(e.target.checked)}
                  />
                  <div className={`block w-10 h-6 rounded-full transition-colors ${includeOthersInGross ? 'bg-green-500' : 'bg-gray-600'}`}></div>
                  <div className={`dot absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${includeOthersInGross ? 'transform translate-x-4' : ''}`}></div>
                </div>
              </label>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="bg-gray-600 text-white text-xs lg:text-sm">
                <th className="min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-left font-semibold sticky lg:static left-0 bg-gray-600">
                  Metric
                </th>
                {MONTH_NAMES.map(month => (
                  <th key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right font-semibold">
                    {month}
                  </th>
                ))}
                <th className="min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right font-bold bg-gray-700">
                  YTD
                </th>
                <th className="min-w-[70px] lg:min-w-0 lg:w-[5%] px-2 lg:px-3 py-2 text-right font-bold bg-gray-700">
                  Profit %
                </th>
              </tr>
            </thead>
            <tbody>
              {/* Total Gross */}
              <tr className="bg-green-50 border-b border-gray-200">
                <td className="min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-semibold text-green-900 sticky lg:static left-0 bg-green-50">
                  Total Gross
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right text-xs lg:text-sm text-green-900">
                    {formatCurrency(getMonthValue(grandTotals.totalGross.monthly, month))}
                  </td>
                ))}
                <td className="min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold text-green-900 bg-green-100">
                  {formatCurrency(grandTotals.totalGross.ytd)}
                </td>
                <td className="min-w-[70px] lg:min-w-0 lg:w-[5%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm text-green-900 bg-green-100">
                </td>
              </tr>

              {/* Total Expenses */}
              <tr className="bg-orange-50 border-b border-gray-200">
                <td className="min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-2 text-xs lg:text-sm font-semibold text-orange-900 sticky lg:static left-0 bg-orange-50">
                  Total Expenses
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className="min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-2 text-right text-xs lg:text-sm text-orange-900">
                    {formatCurrency(getMonthValue(grandTotals.totalExpenses.monthly, month))}
                  </td>
                ))}
                <td className="min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm font-bold text-orange-900 bg-orange-100">
                  {formatCurrency(grandTotals.totalExpenses.ytd)}
                </td>
                <td className="min-w-[70px] lg:min-w-0 lg:w-[5%] px-2 lg:px-3 py-2 text-right text-xs lg:text-sm text-orange-900 bg-orange-100">
                </td>
              </tr>

              {/* Operating Profit */}
              <tr className={grandTotals.operatingProfit.ytd >= 0 ? 'bg-emerald-100' : 'bg-red-100'}>
                <td className={`min-w-[200px] lg:min-w-0 lg:w-[20%] px-2 lg:px-3 py-3 text-xs lg:text-sm font-bold sticky lg:static left-0 ${grandTotals.operatingProfit.ytd >= 0 ? 'text-emerald-900 bg-emerald-100' : 'text-red-900 bg-red-100'}`}>
                  Total Operating Profit
                </td>
                {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                  <td key={month} className={`min-w-[70px] lg:min-w-0 lg:w-[5.5%] px-1 py-3 text-right text-xs lg:text-sm font-semibold ${grandTotals.operatingProfit.ytd >= 0 ? 'text-emerald-900' : 'text-red-900'}`}>
                    {formatCurrency(getMonthValue(grandTotals.operatingProfit.monthly, month))}
                  </td>
                ))}
                <td className={`min-w-[90px] lg:min-w-0 lg:w-[8%] px-2 lg:px-3 py-3 text-right text-sm lg:text-base font-bold ${grandTotals.operatingProfit.ytd >= 0 ? 'text-emerald-900 bg-emerald-200' : 'text-red-900 bg-red-200'}`}>
                  {formatCurrency(grandTotals.operatingProfit.ytd)}
                </td>
                <td className={`min-w-[70px] lg:min-w-0 lg:w-[5%] px-2 lg:px-3 py-3 text-right text-sm lg:text-base font-bold ${grandTotals.operatingProfit.ytd >= 0 ? 'text-emerald-900 bg-emerald-200' : 'text-red-900 bg-red-200'}`}>
                  {formatPercent(grandTotals.operatingProfit.ytd, grandTotals.totalGross.ytd)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>}

      {/* Department Sections */}
      {visibleDepartments.map(dept => renderDepartment(
        dept,
        grandTotals.totalGross.ytd,
        accountDataMap,
        docGrossMap,
        unitCountsData,
        expandedDepartments,
        expandedCategories,
        toggleDepartment,
        toggleCategory,
        formatCurrency
      ))}
    </div>
  );
};
